import React, { Suspense, useState } from 'react';
import { Popover, Spin, Tooltip } from 'antd';
import { PlusOutlined, SmileOutlined } from '@ant-design/icons';
import { DEFAULT_EMOJIS, ReactionsSummary } from './constants';
import { useEngagementTranslations, useEngagementZIndex } from './EngagementTranslationsContext';

// the full emoji picker - loaded only when a user opens it
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

interface Props {
  summary?: ReactionsSummary | null;
  /** the quick reactions row (WhatsApp style) - any other emoji comes from the full picker */
  emojis?: string[];
  /** the emoji clicked - the same as mine means "remove" (handled by the caller / server) */
  onReact: (emoji: string) => void;
  disabled?: boolean;
  size?: 'small' | 'default';
}

/** the quick reactions (WhatsApp style) with a "more" button for the full emoji picker, opened by `children` */
export const ReactionPicker: React.FC<{
  emojis?: string[];
  /** the caller's reaction - highlighted, picking it again removes it */
  selected?: string | null;
  onPick: (emoji: string) => void;
  children: React.ReactElement;
}> = ({ emojis = DEFAULT_EMOJIS, selected, onPick, children }) => {
  const t = useEngagementTranslations();
  const zIndex = useEngagementZIndex();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isFullPicker, setIsFullPicker] = useState(false);

  const openPicker = (open: boolean) => {
    setIsPickerOpen(open);
    if (!open) {
      setIsFullPicker(false);
    }
  };

  const pick = (emoji: string) => {
    openPicker(false);
    onPick(emoji);
  };

  const quickRow = (
    <div className="engagement-emoji-picker">
      {emojis.map((emoji) => (
        <button
          type="button"
          key={emoji}
          className={`engagement-emoji-picker__item ${selected === emoji ? 'is-mine' : ''}`}
          onClick={() => pick(emoji)}
        >
          {emoji}
        </button>
      ))}
      <button
        type="button"
        className="engagement-emoji-picker__item engagement-emoji-picker__more"
        aria-label={t?.moreEmojis}
        title={t?.moreEmojis}
        onClick={() => setIsFullPicker(true)}
      >
        <PlusOutlined />
      </button>
    </div>
  );

  const fullPicker = (
    <div className="engagement-emoji-full">
      <Suspense fallback={<Spin size="small" />}>
        <EmojiPicker
          onEmojiClick={(data) => pick(data.emoji)}
          lazyLoadEmojis
          width={320}
          height={380}
          previewConfig={{ showPreview: false }}
          searchPlaceholder={t?.searchEmoji || undefined}
        />
      </Suspense>
    </div>
  );

  return (
    <Popover
      content={isFullPicker ? fullPicker : quickRow}
      trigger="click"
      open={isPickerOpen}
      onOpenChange={openPicker}
      overlayClassName="engagement-emoji-popover"
      zIndex={zIndex}
      destroyTooltipOnHide
    >
      {children}
    </Popover>
  );
};

/** every emoji people used, not only the quick ones - most used first */
const usedEmojis = (summary?: ReactionsSummary | null) => {
  const counts = summary?.counts || {};
  return Object.keys(counts)
    .filter((emoji) => counts[emoji] > 0)
    .sort((a, b) => counts[b] - counts[a]);
};

/**
 * Facebook-style reactions summary floating on a comment bubble's corner: the top 3 emojis and the total, the
 * count per emoji in the tooltip.
 */
export const ReactionPill: React.FC<{ summary?: ReactionsSummary | null }> = ({ summary }) => {
  const t = useEngagementTranslations();
  const zIndex = useEngagementZIndex();
  const used = usedEmojis(summary);
  if (!used.length) return null;
  const counts = summary?.counts || {};
  const total = used.reduce((sum, emoji) => sum + counts[emoji], 0);
  const tooltip = (
    <div className="engagement-reaction-pill__tooltip">
      {used.map((emoji) => (
        <div key={emoji}>{(t?.reactionCount || '{emoji} {count}').replace('{count}', String(counts[emoji])).replace('{emoji}', emoji)}</div>
      ))}
    </div>
  );
  return (
    <Tooltip title={tooltip} zIndex={zIndex}>
      <span className={`engagement-reaction-pill ${summary?.mine ? 'is-mine' : ''}`}>
        <span className="engagement-reaction-pill__emojis">
          {used.slice(0, 3).map((emoji) => (
            <span key={emoji} className="engagement-reaction-pill__emoji">
              {emoji}
            </span>
          ))}
        </span>
        {total > 1 && <span className="engagement-reaction-pill__count">{total}</span>}
      </span>
    </Tooltip>
  );
};

/**
 * WhatsApp-style reactions: a chip per used emoji with its count (mine highlighted), and a "+" that opens the quick
 * reactions with a "more" button for the full emoji picker. One reaction per user - picking another replaces it,
 * clicking mine again removes it.
 */
const ReactionBar: React.FC<Props> = ({ summary, emojis = DEFAULT_EMOJIS, onReact, disabled, size = 'default' }) => {
  const t = useEngagementTranslations();
  const zIndex = useEngagementZIndex();
  const counts = summary?.counts || {};
  const used = usedEmojis(summary);

  const react = (emoji: string) => {
    if (!disabled) {
      onReact(emoji);
    }
  };

  return (
    <div className={`engagement-reactions engagement-reactions--${size}`}>
      {used.map((emoji) => {
        const isMine = summary?.mine === emoji;
        const fill = (text?: string) =>
          (text || '').replace('{count}', String(counts[emoji])).replace('{emoji}', emoji);
        const tooltip = [
          fill(t?.reactionCount),
          !disabled && (isMine ? t?.removeReaction : fill(t?.addReaction)),
        ]
          .filter(Boolean)
          .join(' — ');
        return (
          <Tooltip key={emoji} title={tooltip} zIndex={zIndex}>
            <button
              type="button"
              className={`engagement-reaction-chip ${isMine ? 'is-mine' : ''}`}
              onClick={() => react(emoji)}
              disabled={disabled}
            >
              <span className="engagement-reaction-chip__emoji">{emoji}</span>
              <span className="engagement-reaction-chip__count">{counts[emoji]}</span>
            </button>
          </Tooltip>
        );
      })}
      {!disabled && (
        <ReactionPicker emojis={emojis} selected={summary?.mine} onPick={react}>
          <Tooltip title={t?.react} zIndex={zIndex}>
            <button type="button" className="engagement-reaction-add" aria-label={t?.react}>
              <SmileOutlined />
              <span className="engagement-reaction-add__plus">+</span>
            </button>
          </Tooltip>
        </ReactionPicker>
      )}
    </div>
  );
};

export default ReactionBar;
